import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton, ErrorButton } from "../UI";
import AgGridWrapper from '../UI/AgGrid';
import { getProject, updateProject } from '../../utils/APIs';

export default function ProjectPromptsModal({ token, organizationId, projectId, onClose, onUpdated }) {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [draft, setDraft] = useState('');
    const [editing, setEditing] = useState(null);
    const request = useRef(null);
    const input = useRef(null);
    useEffect(() => () => request.current?.abort(), []);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        getProject(token, organizationId, projectId, controller.signal)
            .then((result) => { if (!controller.signal.aborted) setProject(result.data); })
            .catch((err) => { if (!controller.signal.aborted) setError(err?.response?.data?.message || err.message); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [token, organizationId, projectId, reload]);

    const save = async (prompts) => {
        if (request.current) return;
        const controller = new AbortController();
        request.current = controller;
        setSaving(true);
        setError('');
        try {
            const result = await updateProject(token, organizationId, projectId, { prompts }, controller.signal);
            if (controller.signal.aborted) return;
            setProject(result.data);
            onUpdated(result.data);
            setDraft('');
            setEditing(null);
        } catch (err) {
            if (!controller.signal.aborted) setError(err?.response?.data?.message || err.message || 'Unable to save prompts.');
        } finally {
            if (!controller.signal.aborted) { setSaving(false); request.current = null; }
        }
    };
    const prompts = project?.prompts || [];
    const submit = (event) => {
        event.preventDefault();
        if (!draft.trim()) return;
        save(editing === null ? [...prompts, draft.trim()] : prompts.map((prompt, index) => index === editing ? draft.trim() : prompt));
    };
    return (
        <PrimaryModal title={`Manage prompts${project ? ` — ${project.name}` : ''}`} zIndex={1300} loading={saving} hideModal={onClose} widthClass="!max-w-3xl">
            <div className="p-6">
                {loading ? <p role="status">Loading prompts…</p> : project && <>
                    <form onSubmit={submit} className="mb-5">
                        <label htmlFor="project-prompt" className="block text-sm font-medium mb-2">{editing === null ? 'Add prompt' : 'Edit prompt'}</label>
                        <textarea ref={input} id="project-prompt" required rows={4} value={draft} disabled={saving} onChange={(event) => setDraft(event.target.value)} className="w-full border border-gray-2 rounded-lg p-3 focus:ring-primary focus:border-primary" />
                        <div className="flex justify-end gap-3 mt-3">
                            {editing !== null && <SecondaryButton type="button" size="sm" disable={saving} handleClick={() => { setEditing(null); setDraft(''); }}>Cancel edit</SecondaryButton>}
                            <PrimaryButton type="submit" size="sm" loading={saving} disable={!draft.trim()}>{editing === null ? 'Add prompt' : 'Update prompt'}</PrimaryButton>
                        </div>
                    </form>
                    <AgGridWrapper tableId="assistant-project-prompts" rowData={prompts.map((prompt, index) => ({ prompt, index }))} getRowId={({ data }) => String(data.index)} rowHeight={64}
                        defaultColDef={{ resizable: true }}
                        columnDefs={[
                            { headerName: 'Prompt', field: 'prompt', flex: 1, minWidth: 200, wrapText: true, autoHeight: true, tooltipField: 'prompt' },
                            { headerName: 'Edit', width: 90, cellStyle: { display: 'flex', alignItems: 'center' }, cellRenderer: ({ data }) => <SecondaryButton size="xs" className="!min-w-0" disable={saving} handleClick={() => { setEditing(data.index); setDraft(data.prompt); input.current?.focus(); }}><Pencil size={18} aria-hidden="true" /><span className="sr-only">Edit prompt {data.index + 1}</span></SecondaryButton> },
                            { headerName: 'Delete', width: 100, cellStyle: { display: 'flex', alignItems: 'center' }, cellRenderer: ({ data }) => <ErrorButton size="xs" className="!min-w-0" disable={saving} handleClick={() => save(prompts.filter((_, index) => index !== data.index))}><Trash2 size={18} aria-hidden="true" /><span className="sr-only">Delete prompt {data.index + 1}</span></ErrorButton> },
                        ]} overlayNoRowsTemplate="No prompts yet."
                    />
                </>}
                {error && <p role="alert" className="text-danger text-sm mt-3">{error}</p>}
                {!project && !loading && <SecondaryButton size="sm" handleClick={() => setReload((value) => value + 1)}>Retry</SecondaryButton>}
                <div className="flex justify-end mt-4"><SecondaryButton size="sm" disable={saving} handleClick={onClose}>Close</SecondaryButton></div>
            </div>
        </PrimaryModal>
    );
}
